import { type RefObject, useLayoutEffect, useState } from 'react'

import { easeInOut } from '@/features/landing/method/carry'

export interface RowGeometry {
    items: Array<{ left: number; width: number }>
    width: number
}

// The index row's geometry — each <li>'s left edge and width, and the
// row's width — read in a layout effect once, again when the fonts land
// and on resize. Both indexes once re-read it on every scrubbed frame,
// a forced layout per frame of every scroll; the ink only needs the
// numbers. offsetLeft is relative to the positioned wrapper.
export function useRowGeometry(
    ref: RefObject<HTMLElement | null>
): RowGeometry | null {
    const [row, setRow] = useState<RowGeometry | null>(null)
    useLayoutEffect(() => {
        const box = ref.current
        if (!box) return
        const read = () =>
            setRow({
                items: Array.from(box.querySelectorAll<HTMLElement>('li')).map(
                    (li) => ({ left: li.offsetLeft, width: li.offsetWidth })
                ),
                width: box.clientWidth,
            })
        read()
        if ('fonts' in document) void document.fonts.ready.then(read)
        window.addEventListener('resize', read)
        return () => window.removeEventListener('resize', read)
    }, [ref])
    return row
}

// The Index stepper's underline: left edge under the current frame (CSS
// transitions it across when the step turns), right edge stretching
// toward the next frame's name as the step progresses.
export function reachInk(
    items: Array<{ left: number; width: number }>,
    i: number,
    t: number,
    containerWidth: number
): { left: number; right: number } {
    const e = items[i]!
    const nx = items[i + 1]
    const ownEnd = e.left + e.width
    const end = nx ? ownEnd + (nx.left - ownEnd) * easeInOut(t) : ownEnd
    return { left: e.left, right: containerWidth - end }
}
